<template>
  <div
    v-if="provider && providerSchemaMeta"
    class="w-100pc h-100pc bg-super-light-blue"
  >
    <v-navigation-drawer
      v-if="!store.state.site.enable_provider_v2"
      v-model="showProviderProfileNavDrawer"
      :location="!$vuetify.display.smAndUp ? 'top' : 'left'"
      :permanent="$vuetify.display.mdAndUp"
      class="bg-super-light-blue bc-extra-light-gray"
    >
      <div
        class="pa-4"
        data-cy="header"
        no-gutters
      >
        <div class="bg-white rounded elevation-1 pa-3">
          <div
            v-text="provider.name"
            class="fs-16 fw-500"
          />

          <div>
            <span class="fs-14 c-black">
              {{ providerAddress() }}
            </span>
          </div>
        </div>

        <div class="d-flex align-center mt-4">
          <div class="fs-12 fw-600">
            {{ $bct('Complete your profile') }}
          </div>

          <v-spacer />

          <div class="fs-12 fw-600">
            {{ `${totalProgress}%` }}
          </div>
        </div>

        <v-progress-linear
          :model-value="totalProgress"
          color="primary"
        />

        <div class="d-flex justify-center mt-4">
          <StandardButton
            :to="{
              name: 'PublicProviderShow',
              params: {
                providerId: provider.id,
              },
            }"
            data-testid="view_public_profile"
            role="secondary"
            target="_blank"
            text="View public profile"
          />
        </div>
      </div>

      <v-divider />

      <div
        class="listbox"
        role="listbox"
      >
        <TabHeader
          v-for="tabOption of profileTabs"
          @click="transition(tabOption.queryValue)"
          :key="tabOption.queryValue"
          :active="tab === tabOption.queryValue"
          :data-cy="`${tabOption.queryValue}_tab`"
          :hide-icon="tabOption.hideIcon"
          :locked="tabOption.locked"
          :progress="progressPercentForSection(tabOption.queryValue)"
          :title="tabOption.title"
        />

        <template v-if="!$vuetify.display.smAndUp">
          <v-divider />
          <div class="d-flex justify-center">
            <v-btn
              @click="toggleProviderProfileNav()"
              icon="close"
              variant="text"
            />
          </div>
        </template>
      </div>
    </v-navigation-drawer>

    <v-main
      v-show="
        store.state.site.enable_provider_v2 ||
        $vuetify.display.smAndUp ||
        !showProviderProfileNavDrawer
      "
      class="fill-height pa-0 bg-white"
    >
      <v-row
        dense
        no-gutters
      >
        <v-col cols="12">
          <div class="h-100pc w-100pc">
            <v-container
              class="px-0 pt-0"
              fluid
            >
              <div
                ref="tabContainer"
                class="focus-very-visible"
                tabindex="0"
              >
                <div>
                  <div
                    v-if="enabledProfileTabValues.includes('overview')"
                    v-show="tab == 'overview'"
                    data-cy="overview-view"
                  >
                    <ProviderOverview
                      @change:publish="changePublished"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="providerOverview"
                      :processing="processing"
                      :provider="provider"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons @next="next()" />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('openings')"
                    v-show="tab == 'openings'"
                    data-cy="openings-view"
                  >
                    <ProgramOpeningsLegacy
                      @change:openings-confirmed="confirmAvailability($event)"
                      @progress="setSectionProgress('openings', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="openings"
                      :openings-confirmed-at="provider.openings_confirmed_at"
                      :provider-id="provider.id"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('openings-and-enrollments')"
                    v-show="tab == 'openings-and-enrollments'"
                    data-cy="openings-enrollments-view"
                  >
                    <OpeningsEnrollmentsIndex
                      @back="back()"
                      @next="next()"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="openingsAndEnrollments"
                      :provider="provider"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('photos')"
                    v-show="tab == 'photos'"
                    data-cy="photos-view"
                  >
                    <AssetManager
                      @load="images.load(true)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      :description="providerSchemaMeta.photos.description"
                      :provider-id="provider.id"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                      category="image"
                      class="mb-4"
                    />
                    <ProviderImages
                      @destroy="destroyAsset"
                      @progress="setSectionProgress('photos', $event)"
                      ref="images"
                      :provider-id="provider.id"
                      class="ma-4"
                      role="manager"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('video')"
                    v-show="tab == 'video'"
                    data-cy="video-view"
                  >
                    <AssetManager
                      @load="video.load()"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      :description="VIRTUAL_TOUR_DESCRIPTION"
                      :provider-id="provider.id"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                      category="video"
                      class="mb-4"
                      title="Virtual tour video"
                    />
                    <VirtualTourGallery
                      @progress="setSectionProgress('video', $event)"
                      ref="video"
                      :provider-id="provider.id"
                      removable
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('description')"
                    v-show="tab == 'description'"
                    data-cy="description-view"
                  >
                    <ProviderDescription
                      @progress="setSectionProgress('description', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="description"
                      :provider="provider"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-show="tab == 'contact'"
                    data-cy="contact-view"
                  >
                    <ContactInformation
                      @progress="setSectionProgress('contact', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="contact"
                      :provider="provider"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('programs')"
                    v-show="tab == 'programs'"
                    data-cy="programs-view"
                  >
                    <ProgramList
                      @progress="setSectionProgress('programs', $event)"
                      @refresh="reload()"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="programs"
                      :age-groups="
                        store.state.ageGroups.filter((ag) => provider.age_group_ids.includes(ag.id))
                      "
                      :description="providerSchemaMeta.programs.description"
                      :provider-id="provider.id"
                      :regular-hours="provider.hours"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('tours')"
                    v-show="tab == 'tours'"
                    data-cy="tours-view"
                  >
                    <ProviderTours
                      v-model="provider"
                      @progress="setSectionProgress('tours', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="tours"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('enrollment_steps')"
                    v-show="tab == 'enrollment_steps'"
                    data-cy="steps-view"
                  >
                    <EnrollmentSteps
                      @error="resetRequestProceedFromEnrollmentSteps"
                      @move-forward="onMoveFromEnrollmentSteps"
                      @progress="setSectionProgress('enrollment_steps', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="enrollment_steps"
                      :provider-id="provider.id"
                      :request-proceed-from-enrollment-steps="requestProceedFromEnrollmentSteps"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('emergency')"
                    v-show="tab == 'emergency'"
                    data-cy="emergency-view"
                  >
                    <EmergencyResponse
                      @progress="setSectionProgress('emergency', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="emergency"
                      :provider="provider"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-show="tab == 'hours'"
                    data-cy="hours-view"
                  >
                    <ProviderHours
                      v-model="provider"
                      @progress="setSectionProgress('hours', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="hours"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('financial')"
                    v-show="tab == 'financial'"
                    data-cy="financial-view"
                  >
                    <PaymentDetails
                      @progress="setSectionProgress('financial', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="financial"
                      :provider="provider"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('enrollment')"
                    v-show="tab == 'enrollment'"
                    data-cy="enrollment-view"
                  >
                    <EnrollmentCount
                      @progress="setSectionProgress('enrollment', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="enrollmentCount"
                      :description="providerSchemaMeta.enrollment.description"
                      :provider-id="provider.id"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('highlights')"
                    v-show="tab == 'highlights'"
                    data-cy="highlights-view"
                  >
                    <ProviderHighlights
                      @progress="setSectionProgress('highlights', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="highlights"
                      :provider="provider"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                      class="mb-4"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('staff')"
                    v-show="tab == 'staff'"
                    data-cy="staff-view"
                  >
                    <StaffMembers
                      @progress="setSectionProgress('staff', $event)"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="staff"
                      :provider-id="provider.id"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                    />
                    <TabButtons
                      @back="back()"
                      @next="next()"
                    />
                  </div>

                  <div
                    v-if="enabledProfileTabValues.includes('licensing')"
                    v-show="tab == 'licensing'"
                    data-cy="licensing-view"
                  >
                    <ProviderLicensing
                      @back="back()"
                      @toggle-provider-profile-nav="toggleProviderProfileNav()"
                      ref="licensing"
                      :provider="provider"
                      :show-provider-profile-nav-btn="showProviderProfileNavBtn"
                      locked
                    />
                    <TabButtons @back="back()" />
                  </div>
                </div>
              </div>
            </v-container>
          </div>
        </v-col>
      </v-row>
    </v-main>

    <VerticalSpacer :min-height="50" />
  </div>
</template>

<script setup>
import { scrollTo } from 'vuetify/lib/composables/goto';
import { useDisplay } from 'vuetify';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'vuex';

import AssetManager from '@/manager/components/provider/AssetManager.vue';
import ContactInformation from '@/shared/components/provider/ContactInformation.vue';
import EmergencyResponse from '@/shared/components/provider/EmergencyResponse.vue';
import EnrollmentCount from '@/shared/components/provider/EnrollmentCount.vue';
import EnrollmentSteps from '@/shared/components/provider/EnrollmentSteps.vue';
import { formatAddress } from '@/shared/services/address';
import OpeningsEnrollmentsIndex from '@/shared/components/provider/OpeningsEnrollmentsIndex.vue';
import PaymentDetails from '@/shared/components/provider/PaymentDetails.vue';
import ProgramList from '@/manager/components/provider/ProgramList.vue';
import ProgramOpeningsLegacy from '@/shared/components/provider/ProgramOpeningsLegacy.vue';
import ProviderDescription from '@/shared/components/provider/ProviderDescription.vue';
import ProviderHighlights from '@/shared/components/provider/ProviderHighlights.vue';
import ProviderHours from '@/shared/components/provider/ProviderHours.vue';
import ProviderImages from '@/shared/components/provider/ProviderImages.vue';
import ProviderLicensing from '@/shared/components/provider/ProviderLicensing.vue';
import ProviderOverview from '@/manager/components/provider/ProviderOverview.vue';
import ProviderTours from '@/manager/components/provider/ProviderTours.vue';
import StaffMembers from '@/shared/components/provider/StaffMembers.vue';
import TabButtons from '@/manager/components/provider/TabButtons.vue';
import TabHeader from '@/shared/components/navigation/TabHeader.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import { useProviderProfileSectionProgress } from '@/manager/composables/useProviderProfileSectionProgress';
import useTerms from '@/shared/composables/useTerms';
import VerticalSpacer from '@/shared/components/VerticalSpacer.vue';
import VirtualTourGallery from '@/shared/components/provider/VirtualTourGallery.vue';

const $bct = inject('translator');

const VIRTUAL_TOUR_DESCRIPTION =
  'Families are interested in seeing your location. ' +
  'Upload a virtual tour video to allow families to check out your space.';

const route = useRoute();
const router = useRouter();
const store = useStore();
const display = useDisplay();
const eventBus = useEventBus();
const { terms } = useTerms();

const providerIdForProgress = computed(() => route.params.providerId);
const {
  clearProviderProgress,
  progressPercentForSection,
  seedSectionKeys,
  setSectionProgress,
  totalProgress,
} = useProviderProfileSectionProgress(providerIdForProgress);

const enrollmentCount = ref(null);
const openings = ref(null);
const requestProceedFromEnrollmentSteps = ref(false);
const provider = ref(null);
const providerSchemaMeta = ref(null);
const processing = ref(false);
const changed = ref(false);
const isLoading = ref(false);
const images = ref(null);
const video = ref(null);

const { api, updateResource } = useApi({ processing, target: provider });

const showProviderProfileNavDrawer = ref(undefined);

const tab = computed(() => {
  const queryTab = route.query.tab;
  if (queryTab !== undefined && queryTab !== null && queryTab !== '') {
    return queryTab;
  }
  if (store.state.site.enable_provider_v2) return '';
  return display.smAndDown.value ? false : '';
});

onBeforeRouteLeave((_to, from, next) => {
  if (from.params.providerId) {
    clearProviderProgress(from.params.providerId);
  }
  next();
});

watch(
  () => route.params.providerId,
  (_newProviderId, previousProviderId) => {
    if (previousProviderId) clearProviderProgress(previousProviderId);

    provider.value = null;
    void reload();
  },
  { immediate: true },
);

watch(
  provider,
  () => {
    if (!isLoading.value && provider.value !== null) {
      changed.value = true;
    }
  },
  { deep: true },
);

watch(tab, (newValue) => {
  if (newValue === 'enrollment_steps') resetRequestProceedFromEnrollmentSteps();
});

const profileTabs = computed(() => {
  return store.getters.providerProfileNavOptions(provider.value, terms.value)?.tabs;
});

const enabledProfileTabValues = computed(() => {
  return profileTabs.value.map((tab) => tab.queryValue);
});

const showProviderProfileNavBtn = computed(() => {
  return !store.state.site.enable_provider_v2 && display.smAndDown.value;
});

function onMoveFromEnrollmentSteps() {
  void next({ canProceedFromEnrollmentSteps: true });
}

function resetRequestProceedFromEnrollmentSteps() {
  requestProceedFromEnrollmentSteps.value = false;
}

async function back() {
  await Promise.all([save(), enrollmentCount.value?.save(), openings.value?.save()]);
  const currentIndex = enabledProfileTabValues.value.indexOf(tab.value);

  if (currentIndex > 0) {
    return transition(enabledProfileTabValues.value[currentIndex - 1]);
  }

  return enabledProfileTabValues.value[0];
}

function changePublished(newValue) {
  void update({ published: newValue });
}

async function confirmAvailability(message) {
  isLoading.value = true;
  const response = await api.provider.update(provider.value.id, {
    openings_confirmed: true,
    openings_confirmed_by_id: store.state.profile.id,
  });
  eventBus.chime(message);
  provider.value = response.data;
  await nextTick();
  isLoading.value = false;
}

function destroyAsset(asset) {
  api.manager.provider.asset.destroy(provider.value.id, asset.id, () => {
    processing.value = false;
    eventBus.chime('Photo removed.');
    images.value.load();
  });
}

function getAppBarChip() {
  if (provider.value.closed_status === 'Open') return { color: 'green', text: 'Open' };
  return { color: 'red', text: provider.value.closed_status };
}

function load() {
  void reload();
}

async function reload() {
  isLoading.value = true;
  api.manager.provider.get(route.params.providerId, async (response) => {
    provider.value = response.data;
    providerSchemaMeta.value = store.state.schemas[provider.value.schema_id]?.meta;
    store.commit('setCurrentPageTitle', provider.value.name);
    store.commit('setAppBarChip', getAppBarChip());
    seedSectionKeys(enabledProfileTabValues.value);

    if (!tab.value || !enabledProfileTabValues.value.includes(tab.value)) {
      await router.replace({ query: { tab: enabledProfileTabValues.value[0] } });
    }

    await nextTick();
    changed.value = false;
    isLoading.value = false;
  });
}

async function next({ canProceedFromEnrollmentSteps = false } = {}) {
  if (tab.value === 'enrollment_steps') {
    requestProceedFromEnrollmentSteps.value = true;

    if (canProceedFromEnrollmentSteps === false) return null;
  }

  await Promise.all([save(), enrollmentCount.value?.save(), openings.value?.save()]);

  const currentIndex = enabledProfileTabValues.value.indexOf(tab.value);
  if (currentIndex < enabledProfileTabValues.value.length - 1) {
    return transition(enabledProfileTabValues.value[currentIndex + 1]);
  }
  return enabledProfileTabValues.value[enabledProfileTabValues.value.length - 1];
}

function providerAddress() {
  if (!provider.value) return null;
  return formatAddress(provider.value.address1, provider.value.city);
}

async function save() {
  if (!changed.value) return;
  isLoading.value = true;
  const result = await updateResource({
    id: provider.value.id,
    resource: api.provider,
    updateObject: provider.value,
    savedMessage: 'Changes saved',
  });
  await nextTick();
  if (result) {
    changed.value = false;
    store.commit('setAppBarChip', getAppBarChip());
  }
  isLoading.value = false;
}

async function transition(newTab) {
  if (tab.value === newTab) {
    if (showProviderProfileNavBtn.value) toggleProviderProfileNav();
    return;
  }

  await router.replace({ query: { tab: newTab } });
  await nextTick();
  scrollTo(0);
}

async function update(newValue) {
  isLoading.value = true;
  await updateResource({
    id: provider.value.id,
    resource: api.provider,
    updateObject: newValue,
  });
  await nextTick();
  isLoading.value = false;
}

function toggleProviderProfileNav() {
  if (store.state.site.enable_provider_v2) return;
  showProviderProfileNavDrawer.value = !showProviderProfileNavDrawer.value;
}

onMounted(load);
</script>
